import {
  UserEventMessageType,
  UserEventType,
  websocket,
  generateUserEventMessage,
  UserEventMessage,
  UserSessionTrackingData,
  UserOwnedLobbiesData,
} from '@playcastdotio/Messaging';
import { sendMessage } from './realtimeMessaging';
import { registerPendingRequest } from './realtimeMessaging/handlers/userEventsMessage';
import { userEventsState } from '../stores/globalStores';
import { toast } from 'sonner';

export const updateState = (
  response: UserEventMessage & { type: UserEventMessageType.USEREVENT_FETCH_RESPONSE | UserEventMessageType.USEREVENT_SERVER_PUSH },
) => {
  if (response.type === UserEventMessageType.USEREVENT_FETCH_RESPONSE) {
    // Update the user events state with the fetched data
    const events = response.events || [];
    const now = Date.now();

    // Check for specific event types
    const mailingListSubscribed = events.some((event: any) => event.eventType === UserEventType.MAILING_LIST_SUBSCRIBE);
    const betaFlagOptedIn = events.some((event: any) => event.eventType === UserEventType.BETA_FLAG_OPT_IN);

    // Look for session tracking data
    const sessionTrackingEvent = events.find((event: any) => event.eventType === UserEventType.USER_SESSION_TRACKING);
    const sessionTracking = sessionTrackingEvent ? (sessionTrackingEvent.eventData as UserSessionTrackingData) : null;

    const ownedLobbies = events.find((event: any) => event.eventType === UserEventType.USER_OWNED_LOBBIES);
    const ownedLobbiesData = ownedLobbies ? (ownedLobbies.eventData as UserOwnedLobbiesData) : null;

    userEventsState.updateState({
      isLoading: false,
      lastFetched: now,
      mailingListSubscribed,
      betaFlagOptedIn,
      sessionTracking,
      ownedLobbies: ownedLobbiesData?.ownedLobbies ?? null,
    });
  } else if (response.type === UserEventMessageType.USEREVENT_SERVER_PUSH) {
    // Update the user events state with the pushed data
    const event = response.eventData;
    if (response.eventType === UserEventType.USER_OWNED_LOBBIES) {
      const ownedLobbiesData = event as UserOwnedLobbiesData;
      userEventsState.updateState({
        ...userEventsState.currentState,
        ownedLobbies: ownedLobbiesData?.ownedLobbies ?? null,
      });
    }
  }
};

/**
 * Query the user's current event status (mailing list, beta flags, etc.)
 * @param eventTypes - Optional array of specific event types to query
 * @returns Promise that resolves when the query is complete
 */
export const queryUserEventStatus = async (eventTypes?: UserEventType[]): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Set loading state
      userEventsState.updateState({
        ...userEventsState.currentState,
        isLoading: true,
      });

      // Generate correlation ID for tracking the response
      const correlationId = `user_events_query_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

      // Register the pending request with the handler
      registerPendingRequest(
        correlationId,
        (response: UserEventMessage & { type: UserEventMessageType.USEREVENT_FETCH_RESPONSE }) => {
          updateState(response);

          resolve();
        },
        (error: Error) => {
          userEventsState.updateState({
            ...userEventsState.currentState,
            isLoading: false,
          });
          console.error('Failed to query user event status:', error);
          reject(error);
        },
        10000, // 10 second timeout
      );

      // Create the user event message for fetching events
      const message = generateUserEventMessage(UserEventMessageType.USEREVENT_FETCH, {
        eventTypes,
        limit: 100,
        offset: 0,
        correlationId,
      });

      // Send the message
      sendMessage(message);
    } catch (error) {
      userEventsState.updateState({
        ...userEventsState.currentState,
        isLoading: false,
      });
      console.error('Error querying user event status:', error);
      reject(error);
    }
  });
};

/**
 * Sign up a user for the Pavilion Launch mailing list
 * @param email - User's email address
 * @param preferences - Optional email preferences
 * @returns Promise that resolves when the signup is processed
 */
export const signupForPavilionLaunch = async (preferences?: Record<string, boolean>): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Show loading toast
      const toastId = toast.loading('Signing up for Pavilion launch updates...', {
        style: {
          color: '#ffffffaa',
        },
      });

      // Generate correlation ID for tracking the response
      const correlationId = `pavilion_signup_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

      // Register the pending request with the handler
      registerPendingRequest(
        correlationId,
        () => {
          toast.success('Successfully signed up for Pavilion launch updates!', {
            id: toastId,
            style: {
              color: '#00aa00',
            },
          });

          // Update local state to reflect the subscription
          userEventsState.updateState({
            ...userEventsState.currentState,
            mailingListSubscribed: true,
          });

          resolve();
        },
        (error: Error) => {
          toast.error(error.message || 'Failed to sign up for mailing list', {
            id: toastId,
            style: {
              color: '#aa0000',
            },
          });
          reject(error);
        },
        10000, // 10 second timeout
      );

      // Create the user event message for mailing list subscription
      const message = generateUserEventMessage(UserEventMessageType.USEREVENT_SEND, {
        eventType: UserEventType.MAILING_LIST_SUBSCRIBE,
        eventData: {
          listId: 'pavilion-launch',
          listName: 'Pavilion Launch Updates',
          preferences: {
            announcements: true,
            earlyAccess: true,
            productUpdates: true,
            ...preferences, // Allow custom preferences to override defaults
          },
        },
        correlationId,
      });

      // Send the message
      sendMessage(message);
    } catch (error) {
      console.error('Error signing up for pavilion launch:', error);
      toast.error('An error occurred while signing up. Please try again.', {
        style: {
          color: '#aa0000',
        },
      });
      reject(error);
    }
  });
};

/**
 * Send a user analytics event
 * @param eventName - Name of the analytics event
 * @param properties - Event properties/data
 */
export const trackAnalyticsEvent = async (
  eventName: string,
  properties?: Record<string, unknown>,
): Promise<void> => {
  try {
    const message = generateUserEventMessage(UserEventMessageType.USEREVENT_SEND, {
      eventType: UserEventType.USER_ANALYTICS_EVENT,
      eventData: {
        eventName,
        properties,
      },
    });

    sendMessage(message);
  } catch (error) {
    console.error('Error tracking analytics event:', error);
    // Don't show error toasts for analytics - fail silently
  }
};
